import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api, onAuthChange, refreshSession } from "./api";
import type { CRMRole, Me } from "./types";

type Status = "checking" | "signed_out" | "signed_in";

const SessionContext = createContext<{ status: Status; setStatus: (s: Status) => void }>({ status: "checking", setStatus: () => {} });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const qc = useQueryClient();
  useEffect(() => {
    // Restore a session from the refresh cookie on page load.
    refreshSession().then((ok) => setStatus(ok ? "signed_in" : "signed_out"));
    return onAuthChange((signedIn) => {
      setStatus(signedIn ? "signed_in" : "signed_out");
      if (!signedIn) qc.clear();
    });
  }, [qc]);
  return <SessionContext.Provider value={{ status, setStatus }}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);

export function useMe() {
  const { status } = useSession();
  return useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/v1/me"), enabled: status === "signed_in", staleTime: 60_000 });
}

// What the UI may show. The server enforces the same rules; this only hides
// controls that would be rejected.
const can: Record<CRMRole, string[]> = {
  crm_admin: ["agents", "workspace", "knowledge", "team", "contacts_all", "tasks_all", "delete", "pipelines", "automations", "brand", "email", "import", "bookings"],
  crm_manager: ["knowledge", "contacts_all", "tasks_all", "pipelines", "automations", "brand", "email", "bookings", "import"],
  crm_employee: ["email"],
  crm_analyst: ["contacts_all", "readonly"],
};

export function useOrg() {
  const { orgId } = useParams();
  const me = useMe();
  const org = me.data?.organizations.find((o) => o.id === orgId);
  const role = org?.crm_role ?? null;
  return {
    orgId: orgId!,
    org,
    role,
    memberId: org?.member_id,
    base: `/v1/orgs/${orgId}`,
    allows: (feature: string) => (role ? can[role].includes(feature) : false),
  };
}
